import React, { useContext } from 'react';
import { ToastContainer } from 'react-toastify';

import { LocaleContext } from '@rsc/contexts/LocaleContext';
import ReferralCode from '@rsc/ui-components/referral-program/ReferralCode';
import ReferralLink from '@rsc/ui-components/referral-program/ReferralLink';
import SocialShare from '@rsc/ui-components/referral-program/SocialShare';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';

interface GetInternalLinkProps {
  reference?: {
    _type: string;
    store?: {
      slug: {
        current;
      };
      productType: string;
    };
    slug?: {
      current;
    };
  };
  slug?: string;
  currency?: string;
  anchor?: string;
}

type GetInternalLinkFunction = (props: GetInternalLinkProps) => string;

interface SuccessMessageProps {
  referralCode: string;
  getInternalLink: GetInternalLinkFunction;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({
  referralCode,
  getInternalLink,
}) => {
  const { tt } = useUiComponentsInternationalContext();
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';

  return (
    <div role="alert">
      <ToastContainer />
      <h2>
        {tt(
          s =>
            s.blockBuilderReferralFormComponent.youAreAllSetSuccessMessageHeader
        )}
      </h2>
      <div className="flex flex-col self-stretch gap-2">
        <p className="m-0">
          {tt(
            s => s.blockBuilderReferralFormComponent.referralCodeInfoMessage,
            {
              link: (
                <LocalizedLink to="/account/referral-program">
                  {tt(
                    s =>
                      s.blockBuilderReferralFormComponent
                        .referralCodeInfoMessageLink
                  )}
                </LocalizedLink>
              ),
              discountAmount: currency === 'CAD' ? 'CA$100' : '$100',
            }
          )}
        </p>

        <ReferralCode referralCode={referralCode} />
        <h2 className="text-lg m-0">
          {tt(
            s =>
              s.blockBuilderReferralFormComponent
                .shareReferralLinkWithFriendsHeader
          )}
        </h2>
        <ReferralLink
          referralCode={referralCode}
          getInternalLink={getInternalLink}
        />
        <SocialShare
          getInternalLink={getInternalLink}
          referralCode={referralCode}
        />

        <div>
          <h3>
            {tt(s => s.blockBuilderReferralFormComponent.howItWorksHeader)}
          </h3>
          <ul className="ml-2 space-y-3">
            <li>
              {tt(s => s.blockBuilderReferralFormComponent.spreadLoveListItem, {
                label: (
                  <strong>
                    {tt(
                      s =>
                        s.blockBuilderReferralFormComponent
                          .spreadLoveListItemLabel
                    )}
                  </strong>
                ),
              })}
            </li>
            <li>
              {tt(
                s =>
                  s.blockBuilderReferralFormComponent.petSafetyForLessListItem,
                {
                  label: (
                    <strong>
                      {tt(
                        s =>
                          s.blockBuilderReferralFormComponent
                            .petSafetyForLessListItemLabel
                      )}
                    </strong>
                  ),
                  discountAmount: currency === 'CAD' ? 'CA$100' : '$100',
                }
              )}
            </li>
            <li>
              {tt(
                s =>
                  s.blockBuilderReferralFormComponent.reapYourRewardsListItem,
                {
                  label: (
                    <strong>
                      {tt(
                        s =>
                          s.blockBuilderReferralFormComponent
                            .reapYourRewardsListItemLabel
                      )}
                    </strong>
                  ),
                  discountAmount: currency === 'CAD' ? 'CA$100' : '$100',
                }
              )}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SuccessMessage;
